import { Component } from "vue";
import DisplayConditionRule from "@/modules/rule_engine/components/conditions/DisplayConditionRule.vue";
import DisplayGroupRule from "@/modules/rule_engine/components/DisplayGroupRule.vue";
import DisplayClampVariableRule from "@/modules/rule_engine/components/DisplayClampVariableRule.vue";
import DisplayLoadMyWheelsBasePriceRule from "@/modules/rule_engine/components/client_specific/my_wheels/DisplayLoadMyWheelsBasePriceRule.vue";
import ConfigureConditionRuleDialog from "@/modules/rule_engine/components/conditions/ConfigureConditionRuleDialog.vue";
import ConfigureClampVariableRuleDialog from "@/modules/rule_engine/components/ConfigureClampVariableRuleDialog.vue";
import ConfigureLoadMyWheelsBasePriceRuleDialog from "@/modules/rule_engine/components/client_specific/my_wheels/ConfigureLoadMyWheelsBasePriceRuleDialog.vue";
import RuleType = App.Data.RuleEngine.Rules.RuleType;

/**
 * Which components render and edit a rule type.
 *
 * `edit: null` means the type is not edited in a dialog — the builder handles it
 * another way (`rule_set` navigates into the group instead).
 */
export type RuleTypeComponents = {
	display: Component;
	edit: Component | null;
};

/**
 * The single place rule types are wired to the view layer.
 *
 * The `satisfies` clause means adding a member to the generated `RuleType`
 * union fails the build here until it is registered, rather than crashing at
 * runtime the way a missing class method did.
 */
export const RULE_TYPE_COMPONENTS = {
	condition: {
		display: DisplayConditionRule,
		edit: ConfigureConditionRuleDialog,
	},
	rule_set: {
		display: DisplayGroupRule,
		edit: null,
	},
	clamp_variable: {
		display: DisplayClampVariableRule,
		edit: ConfigureClampVariableRuleDialog,
	},
	load_my_wheels_base_price: {
		display: DisplayLoadMyWheelsBasePriceRule,
		edit: ConfigureLoadMyWheelsBasePriceRuleDialog,
	},
} satisfies Record<RuleType, RuleTypeComponents>;
/**
 * Rule types a user can add from the "add rule" menu.
 *
 * `rule_set` is excluded: groups are created by grouping existing rules, never
 * added directly. Derived from `RuleType`, so a new backend rule type shows up
 * here automatically.
 */
export type CreatableRuleType = Exclude<RuleType, "rule_set">;

export const displayComponentFor = (type: RuleType): Component =>
	RULE_TYPE_COMPONENTS[type].display;

export const editComponentFor = (type: RuleType): Component | null =>
	RULE_TYPE_COMPONENTS[type].edit;
